import { Box, Form, Layout, Text } from '@shopify/polaris'
import { Formik } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { mutate } from 'swr'

import { Result } from '../common/api'
import {
  ExternalLink,
  ResultToastOrBanner,
  ShowIntercomLink,
  useQuery,
  useShopFlag,
} from '../common/helpers'
import { ReferralCode } from '../common/ReferralCode'
import { FeatureAccessFlag } from '../common/SystemData'
import * as urls from '../common/urls'
import { FormikSaveBar } from '../main/SaveBarProvider'
import { patchUserSettings, UserSettings } from './api'
import { BillingCard } from './BillingCard'
import { CallbacksCard } from './CallbacksCard'
import { DeliveryAddressAutoUpdateCard } from './DeliveryAddressAutoUpdateCard'
import { InventoryManagementCard } from './InventoryManagementCard'
import { KlaviyoCard } from './KlaviyoCard'
import { MerchantApiTokensCard } from './MerchantApiTokensCard'
import { PaywhirlAppsCard } from './PaywhirlAppsCard'
import { PricingPlanCard } from './PricingPlanCard'
import { ProductPageCard } from './ProductPageCard'
import { SubscriptionWidgetCard } from './SubscriptionWidgetCard'
import { SystemNotificationsCard } from './SystemNotificationsCard'
interface Props {
  initialValues: UserSettings
}

const LayoutAnnotatedSection = ({
  title,
  description,
  children,
}: {
  title: string
  description: React.ReactNode
  children: React.ReactNode
}) => {
  const titleWithPadding = (
    <Box paddingInline={{ xs: '400', sm: '0', md: '0', lg: '0', xl: '0' }}>{title}</Box>
  )
  const descriptionWithPadding = (
    <Box paddingInline={{ xs: '400', sm: '0', md: '0', lg: '0', xl: '0' }}>{description}</Box>
  )
  return (
    <Layout.AnnotatedSection title={titleWithPadding} description={descriptionWithPadding}>
      {children}
    </Layout.AnnotatedSection>
  )
}

export const SettingsForm = ({ initialValues }: Props) => {
  const query = useQuery()
  const history = useHistory()

  const klaviyoAuthResult = query.get('klaviyo-auth')
  const klaviyoIntegrationAccess = useShopFlag('klaviyo_integration') as FeatureAccessFlag
  const hasReferralPartnerId = useShopFlag('has_referral_partner_id')
  const isEligibleForReferralProgram = useShopFlag('eligible_for_referral_program')

  const [saveResult, setSaveResult] = useState<Result<any> | null>(null)

  const handleSubmit = useCallback(async (settings: UserSettings, actions: any) => {
    actions.setSubmitting(true)
    const res = await patchUserSettings(settings)
    actions.setSubmitting(false)

    setSaveResult(res)

    if (res.status === 'success' && res.data) {
      mutate(['user-settings'], res.data)
    } else {
      window.scrollTo(0, 0)
    }
  }, [])

  useEffect(() => {
    if (klaviyoAuthResult === 'success') {
      setSaveResult({ status: 'success', message: 'Klaviyo integration enabled', temporary: false })
    }

    if (klaviyoAuthResult === 'error') {
      setSaveResult({
        status: 'error',
        message:
          'There was an error when trying to authorize your Klaviyo account. Please try again or contact PayWhirl support team if this error persists.',
        temporary: false,
      })
    }
  }, [klaviyoAuthResult])

  if (query.get('klaviyo-auth')) {
    history.replace(urls.settingsUrl())
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {(formik) => (
        <>
          <Form onSubmit={formik.handleSubmit}>
            <ResultToastOrBanner result={saveResult} setResult={setSaveResult} />
            <Layout>
              <LayoutAnnotatedSection
                title="Pricing Plan"
                description={
                  <Text as="p">
                    Manage and customize your pricing plan. Questions?{' '}
                    <ShowIntercomLink message="I have questions about pricing.">
                      Chat with us
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <PricingPlanCard />
              </LayoutAnnotatedSection>

              {!hasReferralPartnerId && isEligibleForReferralProgram && (
                <LayoutAnnotatedSection
                  title="Referral Code"
                  description="Enter your PayWhirl partner program referral code."
                >
                  <ReferralCode />
                </LayoutAnnotatedSection>
              )}

              <Layout.AnnotatedSection
                title="Explore Our Apps"
                description={
                  <Text as="p">
                    Discover seamless integrations with apps made by the PayWhirl team. Questions?{' '}
                    <ShowIntercomLink message="I want to know more about PayWhirl apps and integrations.">
                      Let's chat
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <PaywhirlAppsCard />
              </Layout.AnnotatedSection>
              <LayoutAnnotatedSection
                title="Subscription Widget"
                description="Install the subscription widget on the product page to allow custom`6ers to subscribe to your products."
              >
                <SubscriptionWidgetCard />
              </LayoutAnnotatedSection>
              <LayoutAnnotatedSection
                title="Payments"
                description={
                  <Text as="p">
                    Configure dunning settings and manage how payments are retried.{' '}
                    <ShowIntercomLink message="I have questions about failed payment management.">
                      Chat with us
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <BillingCard />
              </LayoutAnnotatedSection>
              <LayoutAnnotatedSection
                title="Inventory Management"
                description={
                  <Text as="p">
                    Set rules for low inventory actions. (
                    <ExternalLink href="https://docs.paywhirl.com/en/articles/7134444-inventory-management-settings-in-paywhirl">
                      learn more
                    </ExternalLink>
                    ) Questions?{' '}
                    <ShowIntercomLink message="I have questions about inventory management settings.">
                      Let's chat
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <InventoryManagementCard />
              </LayoutAnnotatedSection>
              <LayoutAnnotatedSection
                title="Delivery Address Auto-update"
                description={
                  <Text as="p">
                    Automatically update delivery address and method when a customer edits the
                    corresponding address in Shopify.&nbsp;
                    <ShowIntercomLink message="I have questions about the Delivery Address Auto-update.">
                      Chat with us
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <DeliveryAddressAutoUpdateCard />
              </LayoutAnnotatedSection>
              <LayoutAnnotatedSection
                title="Product Page Integration"
                description={
                  <Text as="p">
                    Configure product page subscription interactions. Questions?{' '}
                    <ShowIntercomLink message="I have questions about product page integration settings.">
                      Let's chat
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <ProductPageCard />
              </LayoutAnnotatedSection>
              <LayoutAnnotatedSection
                title="System Notifications"
                description={
                  <Text as="p">
                    Manage email notifications sent to store owners.{' '}
                    <ShowIntercomLink message="I need help with the System Notifications section.">
                      Chat with us
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <SystemNotificationsCard />
              </LayoutAnnotatedSection>
              <LayoutAnnotatedSection
                title="API Tokens"
                description={
                  <Text as="p">
                    Generate and manage API access tokens. (
                    <ExternalLink href="https://api.shop.paywhirl.com/">learn more</ExternalLink>){' '}
                    <ShowIntercomLink message="I want to discuss the API Tokens section.">
                      Chat with us
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <MerchantApiTokensCard />
              </LayoutAnnotatedSection>
              <LayoutAnnotatedSection
                title="Webhooks"
                description={
                  <Text as="p">
                    Configure JSON notifications for subscription events. (
                    <ExternalLink href="https://api.shop.paywhirl.com/">learn more</ExternalLink>){' '}
                    <ShowIntercomLink message="I need assistance with the Webhooks section.">
                      Chat with us
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <CallbacksCard />
              </LayoutAnnotatedSection>
              {klaviyoIntegrationAccess !== 'disabled' && (
                <LayoutAnnotatedSection
                  title="Klaviyo Integration"
                  description={
                    <>
                      Track and analyze subscription events in Klaviyo. (
                      <ExternalLink href="https://docs.paywhirl.com/en/articles/8769188-how-to-send-emails-and-sms-messages-with-our-klaviyo-integration">
                        learn more
                      </ExternalLink>
                      ) Questions?{' '}
                      <ShowIntercomLink message="I have questions about the Klaviyo Integration section.">
                        Let's chat
                      </ShowIntercomLink>
                      .
                    </>
                  }
                >
                  <KlaviyoCard userSettings={initialValues} />
                </LayoutAnnotatedSection>
              )}
            </Layout>
          </Form>
          <FormikSaveBar />
        </>
      )}
    </Formik>
  )
}
